// Lightweight product analytics. Fire-and-forget; never block the UI or surface
// errors to the user. Events go to public.analytics_events (RLS-protected).
//
// Writes are BATCHED. A single learner walking the assessment fires dozens of
// events in a couple of minutes; at 100k onboarded learners one insert per
// event is a needless write storm on the busiest table in the database. Events
// are buffered in memory and flushed as one multi-row insert on a short
// debounce, when the buffer fills, and on tab-hide — so a closed tab never
// costs the funnel its last step.
import { supabase } from "@/integrations/supabase/client";

const SESSION_KEY = "wekit_analytics_session";

/** Flush sooner than this and we are back to one insert per event. */
const FLUSH_DELAY_MS = 2000;
/** A full buffer flushes immediately, so a burst never grows unbounded. */
const MAX_BATCH = 25;

function getSessionId(): string {
  if (typeof window === "undefined") return "ssr";
  try {
    let s = sessionStorage.getItem(SESSION_KEY);
    if (!s) {
      s = `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
      sessionStorage.setItem(SESSION_KEY, s);
    }
    return s;
  } catch {
    return "no-session";
  }
}

export type TrackOptions = {
  surface?: string;
  props?: Record<string, unknown>;
};

type QueuedEvent = {
  user_id: string | null;
  session_id: string;
  event: string;
  surface: string | null;
  props: Record<string, unknown>;
  path: string | null;
};

let queue: QueuedEvent[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;
let sending = false;

/** Push every buffered event. Safe to call at any time; never throws. */
export async function flushAnalytics(): Promise<void> {
  if (timer) { clearTimeout(timer); timer = null; }
  if (sending || queue.length === 0) return;
  const batch = queue;
  queue = [];
  sending = true;
  try {
    const { error } = await supabase.from("analytics_events").insert(batch as never);
    if (error) throw error;
  } catch (err) {
    // Analytics must never cost a learner anything, so a failed batch is
    // dropped rather than retried forever — but only after the newest events
    // have had their turn.
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.warn("[analytics] batch failed", err);
    }
  } finally {
    sending = false;
    if (queue.length) schedule();
  }
}

function schedule(delay = FLUSH_DELAY_MS) {
  if (timer) clearTimeout(timer);
  timer = setTimeout(() => { timer = null; void flushAnalytics(); }, delay);
}

/** Analytics runs only after the visitor chose "Accept all" or ticked analytics. */
export function analyticsAllowed(): boolean {
  try {
    return JSON.parse(localStorage.getItem("wekit-cookie-consent") || "null")?.value === "accepted";
  } catch {
    return false;
  }
}

export function track(event: string, options: TrackOptions = {}): void {
  if (!analyticsAllowed()) return;
  // Fire-and-forget — do not await.
  void (async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      queue.push({
        user_id: user?.id ?? null,
        session_id: getSessionId(),
        event,
        surface: options.surface ?? null,
        props: options.props ?? {},
        path: typeof window !== "undefined" ? window.location.pathname : null,
      });
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.debug("[analytics]", event, options);
      }
      if (queue.length >= MAX_BATCH) void flushAnalytics();
      else schedule();
    } catch (err) {
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.warn("[analytics] failed", event, err);
      }
    }
  })();
}

if (typeof window !== "undefined") {
  window.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") void flushAnalytics();
  });
  window.addEventListener("pagehide", () => { void flushAnalytics(); });
  window.addEventListener("wekit-analytics-off", () => {
    queue = [];
    if (timer) { clearTimeout(timer); timer = null; }
  });
}


// Convenience event creators — keep names canonical so funnels are clean.
export const analytics = {
  journeyView: (props: Record<string, unknown> = {}) =>
    track("journey_view", { surface: "journey", props }),
  journeyNextStepClick: (props: Record<string, unknown>) =>
    track("journey_next_step_click", { surface: "journey", props }),
  journeyMilestoneCtaClick: (props: Record<string, unknown>) =>
    track("journey_milestone_cta_click", { surface: "journey", props }),

  reflectView: (props: Record<string, unknown>) =>
    track("reflect_view", { surface: "reflect", props }),
  reflectStepView: (props: Record<string, unknown>) =>
    track("reflect_step_view", { surface: "reflect", props }),
  reflectStepAdvance: (props: Record<string, unknown>) =>
    track("reflect_step_advance", { surface: "reflect", props }),
  reflectStepBack: (props: Record<string, unknown>) =>
    track("reflect_step_back", { surface: "reflect", props }),
  reflectSubmitAttempt: (props: Record<string, unknown>) =>
    track("reflect_submit_attempt", { surface: "reflect", props }),
  reflectSubmitSuccess: (props: Record<string, unknown>) =>
    track("reflect_submit_success", { surface: "reflect", props }),
  reflectSubmitError: (props: Record<string, unknown>) =>
    track("reflect_submit_error", { surface: "reflect", props }),

  dashboardView: () => track("dashboard_view", { surface: "dashboard" }),
  settingsView: () => track("settings_view", { surface: "settings" }),

  // Offer funnel — impressions and clicks on the sticky bar, the headline links
  // and the Freedom Offer explainer, so discount copy can be tied to revenue.
  offerImpression: (props: Record<string, unknown>) =>
    track("offer_impression", { surface: "offer", props }),
  offerClick: (props: Record<string, unknown>) =>
    track("offer_click", { surface: "offer", props }),
  offerExplainerOpen: (props: Record<string, unknown>) =>
    track("offer_explainer_open", { surface: "offer", props }),
  offerExplainerClose: (props: Record<string, unknown>) =>
    track("offer_explainer_close", { surface: "offer", props }),
};
